<script module lang="ts">
  import type { FilterMode } from "../filter/type.ts";
  import type { FacetValueGroupType } from "../facet-value-group/type.ts";

  const defaultFacetValueLabelOptions: Partial<Record<FilterMode, FacetValueLabelOptions>> = {
    default: { size: "75", showCount: false },
    sheetFirstLevel: { fitContent: true, size: "75", showCount: false },
    sheetSecondLevel: { fitContent: true, size: "75", showCount: false },
  };
  const defaultFacetValueGroupType: FacetValueGroupType = "grid";
  const defaultFacetValueGroupOptions: Partial<Record<FilterMode, FacetValueGroupOptions>> = {
    default: { gridCellMinSize: "79" },
    sheetFirstLevel: { gridCellMinSize: "79" },
    sheetSecondLevel: { gridCellMinSize: "79" },
  };
</script>

<script lang="ts">
  import type { FacetValueLabelOptions } from "../facet-value-label";
  import type { FacetValue } from "../facet-value";
  import type { FacetValueGroupOptions } from "../facet-value-group";
  import type { IntervalFacet, FacetVisibility } from "../facet";
  import { Chip, CustomRange } from "../facet-value";
  import { List, Grid, OverflowBox } from "../facet-value-group";
  import { activeConfiguration, setFilterTitleMode, switchFilterMode } from "../filter";
  import type { Snippet } from "svelte";
  import { Text } from "../facet-value-label";
  import { SingleSelect } from "../facet-value-input";
  import { addNameAndFeatureTracking } from "../facet";
  import type { ConfigurableFilterOptions } from "../filter";
  import type { FilterTitleMode } from "../filter/type.ts";

  interface Props extends ConfigurableFilterOptions {
    data: IntervalFacet;
    visibility: FacetVisibility;
  }

  const { data, visibility, filterConfig }: Props = $props();

  const popularValues = $derived(
    (data.popularValues && data.popularValues.map(addNameAndFeatureTracking(data.id, data.name, 1))) || [],
  );

  const mode = $derived(activeConfiguration.mode);
  const { ...filterConfiguration } = $derived(switchFilterMode(filterConfig, mode));
  const filterTitleMode = $derived(filterConfiguration.filterTitleMode) as FilterTitleMode;

  const { ...userFacetValueLabelOptions } = $derived(
    filterConfiguration.facetConfig.facetValueGroupConfig.facetValueConfig.facetValueLabelConfig,
  );
  const { type: facetValueGroupType = defaultFacetValueGroupType, ...userFacetValueGroupOptions } = $derived(
    filterConfiguration.facetConfig.facetValueGroupConfig,
  );

  /*                                 */
  const facetValueLabelOptions: FacetValueLabelOptions = $derived({
    ...defaultFacetValueLabelOptions[mode],
    ...userFacetValueLabelOptions,
  });

  /*                                 */
  const facetValueGroupOptions: FacetValueGroupOptions = $derived({
    ...defaultFacetValueGroupOptions[mode],
    item: chip,
    ...userFacetValueGroupOptions,
  });

  const facetValueGroupMap: Record<FacetValueGroupType, Snippet<[FacetValue[]]>> = {
    grid,
    list,
    overflowBox,
  };

  $effect(() => {
    setFilterTitleMode(data.parentId, filterTitleMode);
  });

  /*       */
  const featureTracking = data.featureTracking ? { ...data.featureTracking, parentId: data.parentId } : undefined;
</script>

{#snippet customRange(facet: IntervalFacet, facetValue: FacetValue)}
  <CustomRange {...facet} {...facetValue}></CustomRange>
{/snippet}

{#snippet chip(facetValue: FacetValue)}
  <Chip {...facetValue}>
    {#snippet input()}
      <SingleSelect {...facetValue} />
    {/snippet}
    {#snippet children()}
      <Text {...facetValue} {...facetValueLabelOptions} />
    {/snippet}
  </Chip>
{/snippet}

{#snippet grid(values: FacetValue[])}
  <Grid {values} {...facetValueGroupOptions} />
{/snippet}

{#snippet list(values: FacetValue[])}
  <List {values} {...facetValueGroupOptions} />
{/snippet}

{#snippet overflowBox(values: FacetValue[])}
  <OverflowBox {values} {...facetValueGroupOptions} />
{/snippet}

<fieldset
  id={data.id}
  name={data.name}
  class="hc_facet find_facet find_facet--{data.selectionType}"
  data-facet-visibility={visibility}
>
  {#if data.showTitle}
    <legend>
      <span>{data.title}</span>
    </legend>
  {/if}
  {#if filterTitleMode !== "popularValuesExclusion"}
    {@render facetValueGroupMap[facetValueGroupType](popularValues)}
  {/if}
  {@render customRange({ ...data, featureTracking }, data.activeValue)}
</fieldset>

<style lang="scss">
  fieldset {
    min-width: 0;

    span {
      font: var(--oc-semantic-font-copy-100);
      font-weight: 700;
    }
  }
</style>
